import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Cash-drawer denominations + count helpers (used only by this modal).
const DCC_DENOMS = [
  { key: '100',     label: '$100 bill',  value: 100 },
  { key: '50',      label: '$50 bill',   value:  50 },
  { key: '20',      label: '$20 bill',   value:  20 },
  { key: '10',      label: '$10 bill',   value:  10 },
  { key: '5',       label: '$5 bill',    value:   5 },
  { key: '2',       label: '$2 bill',    value:   2 },
  { key: '1',       label: '$1 bill',    value:   1 },
  { key: 'quarter', label: 'Quarter',    value: 0.25, sub: '25¢' },
  { key: 'dime',    label: 'Dime',       value: 0.10, sub: '10¢' },
  { key: 'nickel',  label: 'Nickel',     value: 0.05, sub: '5¢'  },
  { key: 'penny',   label: 'Penny',      value: 0.01, sub: '1¢'  },
];
function dccFmt(n) { return '$' + (Math.round((Number(n) || 0) * 100) / 100).toFixed(2); }
function dccQtyFromDenoms(obj) {
  const out = DCC_DENOMS.map(() => 0);
  if (!obj || typeof obj !== 'object') return out;
  DCC_DENOMS.forEach((d, i) => { out[i] = Math.max(0, Math.floor(Number(obj[d.key]) || 0)); });
  return out;
}
function dccDenomsFromQty(qty) {
  const out = {};
  DCC_DENOMS.forEach((d, i) => { if (qty[i] > 0) out[d.key] = qty[i]; });
  return out;
}
function dccTotal(qty) {
  let t = 0;
  DCC_DENOMS.forEach((d, i) => t += (qty[i] || 0) * d.value);
  return Math.round(t * 100) / 100;
}

export function DailyCashCountModal({ locationId, locationName, businessDate, currentUser, onClose, onSaved }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [count, setCount] = useState(null);
  const [openingFloat, setOpeningFloat] = useState(null);
  const [square, setSquare] = useState(null); // { cc_tips, cash_sales }
  const [staffQty, setStaffQty] = useState(DCC_DENOMS.map(() => 0));
  const [mgrQty, setMgrQty]     = useState(DCC_DENOMS.map(() => 0));
  const [editMode, setEditMode] = useState(false);
  const [certCheck, setCertCheck] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function load() {
    setErr('');
    try {
      const [d, sq] = await Promise.all([
        api('GET', `/api/cash-count/daily?location_id=${locationId}&business_date=${businessDate}`, null, headers),
        api('GET', `/api/cash-count/daily/square?location_id=${locationId}&business_date=${businessDate}`, null, headers)
          .catch(() => ({ cc_tips: null, cash_sales: null })),
      ]);
      setCount(d.count || null);
      setOpeningFloat(d.location?.opening_float_dollars ?? null);
      setSquare(sq);
      if (d.count) {
        setStaffQty(dccQtyFromDenoms(d.count.staff_denominations));
        setMgrQty(dccQtyFromDenoms(d.count.final_denominations || d.count.staff_denominations));
        setNote(d.count.manager_note || '');
      } else {
        setStaffQty(DCC_DENOMS.map(() => 0));
        setMgrQty(DCC_DENOMS.map(() => 0));
        setNote('');
      }
      setEditMode(false);
      setCertCheck(false);
    } catch (e) { setErr(e.message || String(e)); }
  }
  useEffect(() => { load(); }, [locationId, businessDate]);

  async function submitStaff() {
    setBusy(true); setErr('');
    try {
      const denoms = dccDenomsFromQty(staffQty);
      const total = dccTotal(staffQty);
      await api('POST', '/api/cash-count/daily', {
        location_id: locationId,
        business_date: businessDate,
        denominations: denoms,
        drawer_total: total,
      }, headers);
      await load();
      onSaved && onSaved();
    } catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  }

  async function certify() {
    if (!count) return;
    setBusy(true); setErr('');
    try {
      const denoms = dccDenomsFromQty(mgrQty);
      const total = dccTotal(mgrQty);
      await api('PUT', `/api/cash-count/daily/${count.id}/certify`, {
        denominations: denoms,
        drawer_total: total,
        manager_note: note.trim() || null,
      }, headers);
      await load();
      onSaved && onSaved();
    } catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  }

  const totalStaff = dccTotal(staffQty);
  const totalMgr   = dccTotal(mgrQty);
  const impliedCashTips = (count && square?.cash_sales != null)
    ? Math.round(((count.certified_at ? count.final_drawer_total : count.staff_drawer_total) - square.cash_sales) * 100) / 100
    : null;

  const backdrop = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9999,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 12px', overflowY: 'auto',
  };
  const shell = {
    background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 640,
    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
  };

  function renderQtyTable(qty, setQty, disabled, totalLabel, total) {
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #eee', color: '#9e9e9e', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            <th style={{ textAlign: 'left', padding: '6px 4px' }}>Denomination</th>
            <th style={{ textAlign: 'right', padding: '6px 4px' }}>Qty</th>
            <th style={{ textAlign: 'right', padding: '6px 4px' }}>Row total</th>
          </tr>
        </thead>
        <tbody>
          {DCC_DENOMS.map((d, i) => (
            <tr key={d.key} style={{ borderBottom: '1px solid #fafafa' }}>
              <td style={{ padding: '6px 4px', fontWeight: 600 }}>
                {d.label}{d.sub && <span style={{ color: '#9e9e9e', fontWeight: 400, marginLeft: 4 }}>{d.sub}</span>}
              </td>
              <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                <input type="number" inputMode="numeric" min="0" step="1"
                  value={qty[i] || ''} placeholder="0" disabled={disabled}
                  onChange={(e) => {
                    const v = Math.max(0, Math.floor(Number(e.target.value) || 0));
                    setQty(cur => { const nx = [...cur]; nx[i] = v; return nx; });
                  }}
                  style={{ width: 70, padding: '6px 8px', fontSize: 14, textAlign: 'right',
                            border: '1px solid #d0d4d9', borderRadius: 5, background: disabled ? '#f5f5f5' : '#fdfcf7',
                            fontVariantNumeric: 'tabular-nums' }} />
              </td>
              <td style={{ padding: '6px 4px', textAlign: 'right',
                            color: qty[i] * d.value === 0 ? '#bbb' : '#212121',
                            fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                {dccFmt(qty[i] * d.value)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #b48a47', background: '#f6f4ee' }}>
            <td style={{ padding: '10px 4px', fontSize: 11, textTransform: 'uppercase',
                          letterSpacing: 0.5, color: '#6b6b6b' }}>{totalLabel}</td>
            <td></td>
            <td style={{ padding: '10px 4px', textAlign: 'right', fontSize: 18,
                          fontWeight: 800, color: '#2f5d50', fontVariantNumeric: 'tabular-nums' }}>
              {dccFmt(total)}
            </td>
          </tr>
        </tfoot>
      </table>
    );
  }

  const status = !count ? 'draft' : count.certified_at ? 'certified' : 'pending';

  return (
    <div style={backdrop} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={shell}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{locationName} — {businessDate}</div>
            <div style={{ fontSize: 12, color: '#9e9e9e' }}>Daily cash count</div>
          </div>
          <button onClick={onClose} className="btn-secondary" style={{ fontSize: 12 }}>Close</button>
        </div>

        {err && <div style={{ background: '#fee', color: '#b91c1c', padding: 10, borderRadius: 6, fontSize: 12.5, marginBottom: 10 }}>{err}</div>}

        {/* Square + float context strip */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12, fontSize: 12 }}>
          <div style={{ flex: 1, minWidth: 140, padding: 8, background: '#eef5f2', borderRadius: 6 }}>
            <div style={{ color: '#6b6b6b', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Opening float</div>
            <div style={{ fontWeight: 700, color: '#2f5d50', fontVariantNumeric: 'tabular-nums' }}>
              {openingFloat == null ? '—' : dccFmt(openingFloat)}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 140, padding: 8, background: '#fbf7ec', borderRadius: 6 }}>
            <div style={{ color: '#6b6b6b', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Square cash sales (day)</div>
            <div style={{ fontWeight: 700, color: '#8a6b1a', fontVariantNumeric: 'tabular-nums' }}>
              {square?.cash_sales == null ? '—' : dccFmt(square.cash_sales)}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 140, padding: 8, background: '#f2eef8', borderRadius: 6 }}>
            <div style={{ color: '#6b6b6b', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Square CC tips (day)</div>
            <div style={{ fontWeight: 700, color: '#5b3aa8', fontVariantNumeric: 'tabular-nums' }}>
              {square?.cc_tips == null ? '—' : dccFmt(square.cc_tips)}
            </div>
          </div>
        </div>

        {/* Draft — staff form */}
        {status === 'draft' && (
          <>
            <div style={{ fontSize: 12, color: '#6b6b6b', marginBottom: 6 }}>
              No count submitted yet. Enter drawer denominations to submit.
            </div>
            {renderQtyTable(staffQty, setStaffQty, false, 'Drop in safe (total)', totalStaff)}
            <button onClick={submitStaff} disabled={busy} className="btn-primary" style={{ marginTop: 12, width: '100%', padding: '12px 20px', fontSize: 14 }}>
              {busy ? 'Submitting…' : 'Submit for manager review'}
            </button>
          </>
        )}

        {/* Pending — manager view */}
        {status === 'pending' && (
          <>
            <div style={{ background: '#fbf7ec', border: '1px solid #eee2c4', padding: 10, borderRadius: 6, fontSize: 12, marginBottom: 10 }}>
              <strong>{count.submitted_by_name || 'staff'}</strong> submitted <strong>{dccFmt(count.staff_drawer_total)}</strong> at {new Date(count.submitted_at).toLocaleString()}.
            </div>
            {impliedCashTips != null && (
              <div style={{ padding: 8, background: '#f6f4ee', borderRadius: 6, fontSize: 12, marginBottom: 10 }}>
                Implied cash tips: <strong>{dccFmt(impliedCashTips)}</strong>
                {' '}(drawer − Square cash sales)
              </div>
            )}
            {editMode ? renderQtyTable(mgrQty, setMgrQty, false, 'Manager total', totalMgr)
                     : renderQtyTable(mgrQty, setMgrQty, true, 'Manager total', totalMgr)}
            {!editMode && (
              <div style={{ textAlign: 'right', marginTop: 6 }}>
                <button onClick={() => setEditMode(true)} className="btn-secondary" style={{ fontSize: 12 }}>Edit count</button>
              </div>
            )}
            <div style={{ marginTop: 10 }}>
              <label style={{ display: 'block', fontSize: 11, color: '#6b6b6b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Manager note (optional)
              </label>
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Drawer $8 short — traced to a comped refund"
                style={{ width: '100%', padding: 8, fontSize: 13, border: '1px solid #d0d4d9', borderRadius: 5, background: '#fdfcf7', fontFamily: 'inherit', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 12, background: '#fbf7ec', border: '1px solid #eee2c4', borderRadius: 6, marginTop: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, cursor: 'pointer' }}>
                <input type="checkbox" checked={certCheck} onChange={(e) => setCertCheck(e.target.checked)}
                  style={{ transform: 'scale(1.3)', accentColor: '#2f5d50' }} />
                Certify as {dccFmt(totalMgr)}
              </label>
              <button onClick={certify} disabled={!certCheck || busy}
                className="btn-primary" style={{ marginLeft: 'auto', padding: '8px 16px', fontSize: 13 }}>
                {busy ? 'Saving…' : 'Certify'}
              </button>
            </div>
          </>
        )}

        {/* Certified — summary */}
        {status === 'certified' && (
          <>
            <div style={{ background: '#e6f2ec', border: '1px solid #a8d3bb', padding: 12, borderRadius: 6, fontSize: 13, marginBottom: 10 }}>
              <div style={{ fontWeight: 700, color: '#226b4a', marginBottom: 4 }}>Certified · {dccFmt(count.final_drawer_total)}</div>
              <div style={{ fontSize: 11, color: '#4b4636' }}>
                Submitted by {count.submitted_by_name || '?'} · Certified by {count.certified_by_name || '?'} at {new Date(count.certified_at).toLocaleString()}
              </div>
              {count.manager_note && <div style={{ marginTop: 6, fontStyle: 'italic', color: '#37474f' }}>"{count.manager_note}"</div>}
            </div>
            {impliedCashTips != null && (
              <div style={{ padding: 8, background: '#f6f4ee', borderRadius: 6, fontSize: 12, marginBottom: 10 }}>
                Implied cash tips: <strong>{dccFmt(impliedCashTips)}</strong>
                {' '}(certified drawer − Square cash sales)
              </div>
            )}
            {renderQtyTable(mgrQty, setMgrQty, true, 'Certified total', totalMgr)}
          </>
        )}
      </div>
    </div>
  );
}
